// TODO -> handle caching properly

import { TActivity } from "@/types/activity";
import { EErrorCode, TErrorResponse } from "@/types/api-error";
import { TBooking } from "@/types/booking";
import { TCountry } from "@/types/country";
import { TCoupon } from "@/types/coupon";
import { TEvent, TExtendedEvent } from "@/types/event";
import { TOrder } from "@/types/order";
import { TUserPack } from "@/types/pack";
import { EPaymentPrefix } from "@/types/payment-prefix";
import { TSettings } from "@/types/settings";
import { TState } from "@/types/state";
import { TAdminUser, TUser } from "@/types/user";
import { waitForAuthReady } from "./firebase-auth";
import firestore from "./firestore";

const client = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-unused-vars
  get: async <T>(
    url: string,
    props: any,
    tags?: string[],
    _disableCache?: boolean,
    customHeaders?: any
  ) => {
    url = (process.env.NEXT_PUBLIC_API_BASE_URL || "") + url;

    const cacheOptions: RequestInit & {
      next: { tags: string[]; revalidate?: number };
    } = {
      next: {
        tags: tags || [url + JSON.stringify(props)],
      },
    };

    //   if (process.env.ENV === "stage" || process.env.NEXT_PUBLIC_IS_PREVIEW === "true" || disableCache) {
    //     cacheOptions.cache = "no-store";
    //   } else {
    //     cacheOptions.next.revalidate = +(process.env.NEXT_PUBLIC_REVALIDATE_CACHE || 3600);
    //   }

    const user = await waitForAuthReady();
    const token = user ? await user.getIdToken() : "";

    if (user && (!token || typeof token !== 'string')) {
      try {
        await firestore.log({
          user: user.uid || '',
          createdDate: new Date(),
          error: 'INVALID TOKEN'
        })
      } catch { }
    }

    const response = await fetch(
      url + "?" + new URLSearchParams(props).toString(),
      {
        method: "get",
        headers: {
          ...(customHeaders || {}),
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        ...cacheOptions,
        // cache: "no-store",
      } as unknown as RequestInit
    );

    let rawText: string | null = null

    try {
      rawText = await response.text()
    } catch (err) {
      try {
        await firestore.log({
          user: '',
          createdDate: new Date(),
          error: (typeof err === 'string' ? err : JSON.stringify(err)) + ' - url: ' + url
        })
      } catch { }

      return {
        errorStatus: response.status,
        endpoint: url,
        errorMessage: "Failed to read response body",
        errorObject: {},
        errorCode: EErrorCode.GenericError,
      };
    }

    if (response.ok) {
      try {
        return rawText ? JSON.parse(rawText) : ({} as T)
      } catch {
        try {
          await firestore.log({
            user: '',
            createdDate: new Date(),
            error: 'Invalid JSON in successful response' + ' - url: ' + url
          })
        } catch { }

        return {
          errorStatus: response.status,
          endpoint: url,
          errorMessage: "Invalid JSON in successful response",
          errorObject: {},
          errorCode: EErrorCode.GenericError,
        };
      }
    } else {
      let errorResponse;
      try {
        errorResponse = rawText ? JSON.parse(rawText) : {
          errorStatus: response.status,
          endpoint: url,
          errorMessage: 'Empty error body',
          errorObject: {},
          errorCode: EErrorCode.GenericError,
        }
      } catch {
        errorResponse = {
          errorStatus: response.status,
          endpoint: url,
          errorMessage: "GENERIC SDK API ERROR",
          errorObject: {},
          errorCode: EErrorCode.GenericError,
        } as TErrorResponse;
      }

      try {
        if (url.indexOf('decrypt-user') === -1 && response.status !== 400) {
          await firestore.log({
            user: '',
            createdDate: new Date(),
            error: (typeof errorResponse === 'string' ? errorResponse : JSON.stringify(errorResponse)) + ' - url: ' + url
          })
        }
      } catch { }

      return errorResponse;
    }
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post: async <T>(url: string, body: any, customHeaders?: any) => {
    url = (process.env.NEXT_PUBLIC_API_BASE_URL || "") + url;

    const user = await waitForAuthReady();
    const token = user ? await user.getIdToken() : "";

    if (user && (!token || typeof token !== 'string')) {
      try {
        await firestore.log({
          user: user.uid || '',
          createdDate: new Date(),
          error: 'INVALID TOKEN'
        })
      } catch { }
    }

    const response = await fetch(url, {
      method: "post",
      headers: {
        ...(customHeaders || {}),
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      // cache: "no-store",
      body: JSON.stringify(body),
    });

    let rawText: string | null = null

    try {
      rawText = await response.text()
    } catch (err) {
      try {
        await firestore.log({
          user: '',
          createdDate: new Date(),
          error: (typeof err === 'string' ? err : JSON.stringify(err)) + ' - url: ' + url
        })
      } catch { }

      return {
        errorStatus: response.status,
        endpoint: url,
        errorMessage: "Failed to read response body",
        errorObject: {},
        errorCode: EErrorCode.GenericError,
      };
    }

    if (response.ok) {
      try {
        return rawText ? JSON.parse(rawText) : ({} as T)
      } catch {
        try {
          await firestore.log({
            user: '',
            createdDate: new Date(),
            error: 'Invalid JSON in successful response' + ' - url: ' + url
          })
        } catch { }

        return {
          errorStatus: response.status,
          endpoint: url,
          errorMessage: "Invalid JSON in successful response",
          errorObject: {},
          errorCode: EErrorCode.GenericError,
        };
      }
    } else {
      let errorResponse;
      try {
        errorResponse = rawText ? JSON.parse(rawText) : {
          errorStatus: response.status,
          endpoint: url,
          errorMessage: 'Empty error body',
          errorObject: {},
          errorCode: EErrorCode.GenericError,
        }
      } catch {
        errorResponse = {
          errorStatus: response.status,
          endpoint: url,
          errorMessage: "GENERIC SDK API ERROR",
          errorObject: {},
          errorCode: EErrorCode.GenericError,
        };
      }

      try {
        if (url.indexOf('decrypt-user') === -1 && response.status !== 400) {
          await firestore.log({
            user: '',
            createdDate: new Date(),
            error: (typeof errorResponse === 'string' ? errorResponse : JSON.stringify(errorResponse)) + ' - url: ' + url
          })
        }
      } catch { }

      return errorResponse;
    }
  },
};

const settings = {
  baseRoute: "/api/settings",

  get: () => {
    return client.get<{ settings: TSettings } | TErrorResponse>(
      `${settings.baseRoute}/get`,
      {}
    );
  },
};

const countries = {
  baseRoute: "/api/countries",

  getCountries: () => {
    return client.get<{ countries: TCountry[] } | TErrorResponse>(
      `${countries.baseRoute}/get-countries`,
      {}
    );
  },

  getStates: (countryCode: string) => {
    return client.get<{ states: TState[] } | TErrorResponse>(
      `${countries.baseRoute}/get-states`,
      { countryCode }
    );
  },
};

const booking = {
  baseRoute: "/api/booking",

  createBooking: (userBooking: TBooking) => {
    return client.post<{ success: boolean } | TErrorResponse>(
      `${booking.baseRoute}/create-booking`,
      { booking: userBooking }
    );
  },

  createBookingFromAdmin: (userBooking: TBooking) => {
    return client.post<{ success: boolean } | TErrorResponse>(
      `${booking.baseRoute}/create-booking-from-admin`,
      { booking: userBooking }
    );
  },

  updateBooking: (userBooking: Partial<TBooking>, password: string) => {
    return client.post<{ success: boolean } | TErrorResponse>(
      `${booking.baseRoute}/update-booking`,
      { booking: userBooking, password }
    );
  },

  updateBookingFromAdmin: (userBooking: Partial<TBooking>) => {
    return client.post<{ success: boolean } | TErrorResponse>(
      `${booking.baseRoute}/update-booking-from-admin`,
      { booking: userBooking }
    );
  },

  applyCoupon: (coupon: string, eventId: string, people: number) => {
    return client.post<{ coupon: TCoupon } | TErrorResponse>(
      `${booking.baseRoute}/apply-coupon`,
      { coupon, eventId, people }
    );
  },

  getBooking: (id: string, password: string) => {
    return client.get<{ booking: TBooking } | TErrorResponse>(
      `${booking.baseRoute}/get-booking`,
      { id, password }
    );
  },

  getBookingFromAdmin: (id: string) => {
    return client.get<{ booking: TBooking } | TErrorResponse>(
      `${booking.baseRoute}/get-booking-from-admin`,
      { id }
    );
  },

  cancelBooking: (id: string, password: string) => {
    return client.post<{ coupon: string } | TErrorResponse>(
      `${booking.baseRoute}/cancel-booking`,
      { id, password }
    );
  },

  cancelBookingFromAdmin: (id: string) => {
    return client.post<{ success: boolean } | TErrorResponse>(
      `${booking.baseRoute}/cancel-booking-from-admin`,
      { id }
    );
  },

  addPeople: (id: string, otherUsers: TUser[], password: string) => {
    return client.post<{ booking: TBooking } | TErrorResponse>(
      `${booking.baseRoute}/add-people`,
      { id, otherUsers, password }
    );
  },

  makeRegularBooking: (event: TEvent, needsEnglish: boolean) => {
    return client.post<{ success: boolean } | TErrorResponse>(
      `${booking.baseRoute}/make-regular-booking`,
      { event, needsEnglish }
    );
  },

  getBookings: (eventMasterId: string, date: string) => {
    return client.get<{ bookings: TBooking[] } | TErrorResponse>(
      `${booking.baseRoute}/get-bookings`,
      { eventMasterId, date }
    );
  },

  getAllBookings: (onlyConfirmed: boolean, start: string, end: string, cursor: string) => {
    return client.get<
      { bookings: TBooking[]; cursor: string } | TErrorResponse
    >(`${booking.baseRoute}/get-all-bookings`, { onlyConfirmed, start, end, cursor });
  },
};

const coupon = {
  baseRoute: "/api/coupon",

  makePayment: (activity: TActivity) => {
    return client.post<{ coupon: TCoupon } | TErrorResponse>(
      `${coupon.baseRoute}/make-payment`,
      { activity }
    );
  },

  redeem: (voucher: string) => {
    return client.post<{ successMessage: string } | TErrorResponse>(
      `${coupon.baseRoute}/redeem`,
      { coupon: voucher }
    );
  },

  createCoupon: (userCoupon: TCoupon) => {
    return client.post<{ success: boolean } | TErrorResponse>(
      `${coupon.baseRoute}/create-coupon`,
      { coupon: userCoupon }
    );
  },

  getCode: (paymentId: string) => {
    return client.get<{ value: string } | TErrorResponse>(
      `${coupon.baseRoute}/get-code`,
      { paymentId }
    );
  },
};

const user = {
  baseRoute: "/api/user",

  signup: (userParams: TUser) => {
    return client.post<{ success: boolean } | TErrorResponse>(
      `${user.baseRoute}/signup`,
      { user: userParams }
    );
  },

  getUser: (userId: string) => {
    return client.get<{ user: TUser } | TErrorResponse>(
      `${user.baseRoute}/get-user`,
      { userId }
    );
  },

  getUserEvents: () => {
    return client.get<{ events: TExtendedEvent[] } | TErrorResponse>(
      `${user.baseRoute}/get-user-events`,
      {}
    );
  },

  getUserPastEvents: (cursor?: string) => {
    return client.get<
      { events: TExtendedEvent[]; cursor: string } | TErrorResponse
    >(`${user.baseRoute}/get-user-past-events`, { cursor });
  },

  getPastEventsForUser: (userId: string, cursor?: string) => {
    return client.get<
      { events: TExtendedEvent[]; cursor: string } | TErrorResponse
    >(`${user.baseRoute}/get-past-events-for-user`, { userId, cursor });
  },

  getEventsForUser: (userId: string, cursor?: string) => {
    return client.get<
      { events: TExtendedEvent[]; cursor: string } | TErrorResponse
    >(`${user.baseRoute}/get-events-for-user`, { userId, cursor });
  },

  cancelUserEvent: (eventId: string) => {
    return client.post<{ success: boolean } | TErrorResponse>(
      `${user.baseRoute}/cancel-user-event`,
      { eventId }
    );
  },

  getUserActivePack: () => {
    return client.get<{ pack: TUserPack } | TErrorResponse>(
      `${user.baseRoute}/get-user-active-pack`,
      {}
    );
  },

  getUserPastPack: () => {
    return client.get<{ pack: TUserPack } | TErrorResponse>(
      `${user.baseRoute}/get-user-past-pack`,
      {}
    );
  },

  getActivePackForUser: (userId: string) => {
    return client.get<{ pack: TUserPack } | TErrorResponse>(
      `${user.baseRoute}/get-active-pack-for-user`,
      { userId }
    );
  },
};

const security = {
  baseRoute: "/api/security",

  decrypUser: (user: TUser) => {
    return client.post<{ user: TUser } | TErrorResponse>(
      `${security.baseRoute}/decrypt-user`,
      { user }
    );
  },
};

const admin = {
  baseRoute: "/api/admin",

  makeAdmin: (userId: string, makeAdmin: boolean) => {
    return client.post<{ success: boolean } | TErrorResponse>(
      `${admin.baseRoute}/make-admin`,
      { userId, makeAdmin }
    );
  },

  blockAccount: (userId: string, blockAccount: boolean) => {
    return client.post<{ success: boolean } | TErrorResponse>(
      `${admin.baseRoute}/block-account`,
      { userId, blockAccount }
    );
  },

  updateUser: (user: TAdminUser) => {
    return client.post<{ success: boolean } | TErrorResponse>(
      `${admin.baseRoute}/update-user`,
      { user }
    );
  },
};

const payments = {
  baseRoute: "/api/payments",

  getPaymentForm: (amount: number, prefix: EPaymentPrefix, merchantData: string) => {
    return client.get<{ form: any; orderId: string } | TErrorResponse>(
      `${payments.baseRoute}/get-payment-form`,
      { amount, prefix, merchantData }
    );
  },

  processError: (params: {
    Ds_SignatureVersion?: string;
    Ds_MerchantParameters?: string;
    Ds_Signature?: string;
  }) => {
    return client.post<{ paymentPrefix: EPaymentPrefix } | TErrorResponse>(
      `${payments.baseRoute}/process-error`,
      { ...params }
    );
  },
};

const events = {
  baseRoute: "/api/events",

  deleteEvent: (eventId: string, eventMasterId: string, date: string) => {
    return client.post<{ success: boolean } | TErrorResponse>(
      `${events.baseRoute}/delete-event`,
      { eventId, eventMasterId, date }
    );
  },

  deleteEventMaster: (eventMasterId: string) => {
    return client.post<{ success: boolean } | TErrorResponse>(
      `${events.baseRoute}/delete-event-master`,
      { eventMasterId }
    );
  },

  deleteEventMasterEvents: (eventMasterId: string) => {
    return client.post<{ success: boolean } | TErrorResponse>(
      `${events.baseRoute}/delete-event-master-events`,
      { eventMasterId }
    );
  },
};

const orders = {
  baseRoute: "/api/orders",

  getOrder: (orderId: string) => {
    return client.get<{ order: TOrder } | TErrorResponse>(
      `${orders.baseRoute}/get-order`,
      { orderId }
    );
  },

  getOrderByPaymentId: (
    paymentId: string,
    whereFrom: "booking" | "userPack" | "coupon"
  ) => {
    return client.get<{ order: TOrder } | TErrorResponse>(
      `${orders.baseRoute}/get-order-by-payment-id`,
      { paymentId, whereFrom }
    );
  },

  getOrders: (onlyOpen: boolean, cursor: string) => {
    return client.get<{ orders: TOrder[]; cursor: string } | TErrorResponse>(
      `${orders.baseRoute}/get-orders`,
      { onlyOpen, cursor }
    );
  },
};

const media = {
  baseRoute: "/api/media",

  saveImage: (name: string, image: string, folder: string) => {
    return client.post<{ paymentPrefix: EPaymentPrefix } | TErrorResponse>(
      `${media.baseRoute}/save-image`,
      { name, image, folder }
    );
  },
};

const SDK = {
  settings,
  countries,
  booking,
  coupon,
  user,
  security,
  admin,
  payments,
  media,
  events,
  orders,
};

export default SDK;
